const POSITIVE_DEFAULT_SCORE = 78
const DEVELOPING_DEFAULT_SCORE = 52
const RISK_DEFAULT_SCORE = 34

export const normalizeLedgerText = (value) => String(value || '').trim()

export const normalizeLedgerKey = (value) => normalizeLedgerText(value)
  .toLowerCase()
  .replace(/&/g, 'and')
  .replace(/[^a-z0-9]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()

const uniqueTextList = (items = [], limit = 8) => {
  const seen = new Set()
  const result = []

  ;(Array.isArray(items) ? items : []).forEach((item) => {
    const text = normalizeLedgerText(item)
    if (!text) return
    const key = normalizeLedgerKey(text)
    if (!key || seen.has(key)) return
    seen.add(key)
    result.push(text)
  })

  return result.slice(0, limit)
}

const clampPercent = (value) => {
  const number = Number(value)
  if (!Number.isFinite(number)) return 0
  return Math.max(0, Math.min(100, Math.round(number)))
}

const scoreFromRubricItem = (item = {}) => {
  const score = Number(item.score)
  const outOf = Number(item.outOf) || 5
  if (!Number.isFinite(score) || outOf <= 0) return null
  return clampPercent((score / outOf) * 100)
}

const scoreFromPromptFinding = (item = {}) => {
  const score = Number(item.score)
  if (Number.isFinite(score)) {
    if (score <= 5) return clampPercent((score / 5) * 100)
    return clampPercent(score)
  }

  const masterySignal = normalizeLedgerKey(item.masterySignal)
  if (masterySignal.includes('strong') || masterySignal.includes('confident')) return 82
  if (masterySignal.includes('develop') || masterySignal.includes('mixed')) return 56
  if (masterySignal.includes('weak') || masterySignal.includes('low')) return 38
  return null
}

const buildCompetencyId = (courseKey, label) => {
  const competencyKey = normalizeLedgerKey(label)
  if (!competencyKey) return ''
  return `${courseKey || 'general'}__${competencyKey.replace(/\s+/g, '-')}`
}

const normalizeCourseKey = (courseName = '') => normalizeLedgerKey(courseName)

const getEvidenceWeight = (sourceType = '') => {
  if (sourceType === 'assignment-rubric') return 1.5
  if (sourceType === 'assignment-prompt') return 1.15
  if (sourceType === 'assignment-strength') return 1.05
  if (sourceType === 'assignment-growth') return 1.15
  if (sourceType === 'assignment-misconception') return 1.2
  if (sourceType === 'assignment-profile') return 1.2
  if (sourceType === 'firefly-diagnosis') return 1.25
  if (sourceType === 'firefly-topic') return 0.9
  if (sourceType === 'firefly-strength') return 1.05
  if (sourceType === 'firefly-struggle') return 1.1
  if (sourceType === 'firefly-misconception') return 1.15
  if (sourceType === 'firefly-profile') return 1.15
  if (sourceType === 'course-progress') return 0.8
  if (sourceType === 'curriculum-anchor') return 0
  return 1
}

const getStatusFromScore = (score = 0) => {
  if (score >= 85) return 'advanced'
  if (score >= 70) return 'proficient'
  if (score >= 50) return 'developing'
  return 'emerging'
}

const getConfidenceFromEvidence = (evidenceCount = 0, sourceCount = 0) => {
  if (evidenceCount >= 7 && sourceCount >= 3) return 'high'
  if (evidenceCount >= 3 && sourceCount >= 2) return 'medium'
  return 'building'
}

const extractProfileSignals = (items = [], defaultSignal = {}) => (
  (Array.isArray(items) ? items : [])
    .map((item) => {
      if (typeof item === 'string') {
        return {
          label: item,
          evidence: '',
          action: '',
          ...defaultSignal,
        }
      }

      return {
        label: normalizeLedgerText(item?.label || item?.title || item?.topic || item?.name),
        evidence: normalizeLedgerText(item?.evidence || item?.reason || item?.description),
        action: normalizeLedgerText(item?.action || item?.nextStep || item?.guidance || item?.firstMove),
        ...defaultSignal,
      }
    })
    .filter((item) => item.label)
)

const getCurriculumUnits = (scheme = []) => {
  const units = []

  ;(Array.isArray(scheme) ? scheme : []).forEach((week, weekIndex) => {
    const weekLabel = normalizeLedgerText(week?.topic || `Week ${week?.week || weekIndex + 1}`)
    if (weekLabel) {
      units.push({
        label: weekLabel,
        curriculumType: 'week-topic',
        curriculumWeek: normalizeLedgerText(week?.week || weekIndex + 1),
        parentTopic: weekLabel,
      })
    }

    ;(Array.isArray(week?.dailyPlan) ? week.dailyPlan : []).forEach((day, dayIndex) => {
      const focus = normalizeLedgerText(day?.focus || day?.topic || day?.description)
      if (!focus) return
      units.push({
        label: focus,
        curriculumType: 'daily-focus',
        curriculumWeek: normalizeLedgerText(week?.week || weekIndex + 1),
        parentTopic: weekLabel || `Week ${week?.week || weekIndex + 1}`,
        curriculumDay: normalizeLedgerText(day?.day || `Day ${dayIndex + 1}`),
      })
    })
  })

  return units
}

const createEmptyCompetency = ({ id, label, courseName, courseKey }) => ({
  id,
  label,
  competencyKey: normalizeLedgerKey(label),
  score: 0,
  status: 'emerging',
  confidence: 'building',
  evidenceCount: 0,
  sourceCount: 0,
  lastUpdatedAtMs: 0,
  courseName,
  courseKey,
  courses: new Set(courseName ? [courseName] : []),
  sources: new Set(),
  evidenceItems: [],
  curriculumAnchors: new Set(),
  supportingSignals: [],
  riskSignals: [],
  nextMoves: [],
})

const addCompetencyEvidence = (map, courseName, label, evidence = {}) => {
  const normalizedLabel = normalizeLedgerText(label)
  if (!normalizedLabel) return

  const courseKey = normalizeCourseKey(courseName)
  const competencyId = buildCompetencyId(courseKey, normalizedLabel)
  if (!competencyId) return

  if (!map.has(competencyId)) {
    map.set(competencyId, createEmptyCompetency({
      id: competencyId,
      label: normalizedLabel,
      courseName,
      courseKey,
    }))
  }

  const competency = map.get(competencyId)
  competency.courses.add(courseName)

  const normalizedEvidence = {
    sourceType: normalizeLedgerText(evidence.sourceType || 'signal'),
    sourceLabel: normalizeLedgerText(evidence.sourceLabel || evidence.sourceType || 'Signal'),
    summary: normalizeLedgerText(evidence.summary),
    evidenceText: normalizeLedgerText(evidence.evidenceText),
    actionText: normalizeLedgerText(evidence.actionText),
    assignmentTitle: normalizeLedgerText(evidence.assignmentTitle),
    curriculumAnchor: normalizeLedgerText(evidence.curriculumAnchor),
    timestampMs: Number(evidence.timestampMs) || 0,
    score: Number.isFinite(Number(evidence.score)) ? clampPercent(evidence.score) : null,
  }

  competency.evidenceItems.push(normalizedEvidence)

  if (normalizedEvidence.sourceType) competency.sources.add(normalizedEvidence.sourceType)
  if (normalizedEvidence.curriculumAnchor) competency.curriculumAnchors.add(normalizedEvidence.curriculumAnchor)
  if (normalizedEvidence.actionText) competency.nextMoves.push(normalizedEvidence.actionText)

  const summaryText = normalizedEvidence.summary || normalizedEvidence.evidenceText
  if (summaryText) {
    if ((normalizedEvidence.score ?? DEVELOPING_DEFAULT_SCORE) >= 65) {
      competency.supportingSignals.push(summaryText)
    } else {
      competency.riskSignals.push(summaryText)
    }
  }

  competency.lastUpdatedAtMs = Math.max(competency.lastUpdatedAtMs, normalizedEvidence.timestampMs)
}

const finalizeCompetencies = (map) => Array.from(map.values())
  .map((competency) => {
    const weightedEvidence = competency.evidenceItems
      .filter((item) => item.score !== null)
      .map((item) => ({
        ...item,
        weight: getEvidenceWeight(item.sourceType),
      }))
      .filter((item) => item.weight > 0)

    const weightTotal = weightedEvidence.reduce((total, item) => total + item.weight, 0)
    const weightedScore = weightTotal
      ? weightedEvidence.reduce((total, item) => total + (item.score * item.weight), 0) / weightTotal
      : 0

    const status = getStatusFromScore(weightedScore)

    return {
      id: competency.id,
      label: competency.label,
      score: clampPercent(weightedScore),
      status,
      confidence: getConfidenceFromEvidence(
        competency.evidenceItems.length,
        competency.sources.size,
      ),
      evidenceCount: competency.evidenceItems.length,
      sourceCount: competency.sources.size,
      lastUpdatedAtMs: competency.lastUpdatedAtMs,
      courseName: competency.courseName,
      courseKey: competency.courseKey,
      courses: Array.from(competency.courses).filter(Boolean),
      sources: Array.from(competency.sources).filter(Boolean),
      curriculumAnchors: uniqueTextList(Array.from(competency.curriculumAnchors), 3),
      supportingSignals: uniqueTextList(competency.supportingSignals, 3),
      riskSignals: uniqueTextList(competency.riskSignals, 3),
      nextMoves: uniqueTextList(competency.nextMoves, 3),
      latestEvidence: [...competency.evidenceItems]
        .sort((first, second) => second.timestampMs - first.timestampMs)
        .slice(0, 4),
    }
  })
  .filter((item) => item.evidenceCount > 0 || item.curriculumAnchors.length > 0)
  .sort((first, second) => {
    if (second.score !== first.score) return second.score - first.score
    if (second.evidenceCount !== first.evidenceCount) return second.evidenceCount - first.evidenceCount
    return first.label.localeCompare(second.label)
  })

const getTopCompetency = (items = []) => {
  const list = Array.isArray(items) ? items : []
  if (!list.length) return null
  return [...list].sort((first, second) => (
    second.evidenceCount - first.evidenceCount || second.score - first.score
  ))[0]
}

const addAssignmentAssessmentEvidence = (map, courseName, assignment, assessmentDoc) => {
  const assessment = assessmentDoc?.assessment || {}
  const reviewedAtMs = Math.max(
    Number(assessmentDoc?.updatedAtMs) || 0,
    Number(assessmentDoc?.generatedAtMs) || 0,
  )
  const assignmentTitle = normalizeLedgerText(
    assignment?.assignmentTitle || assignment?.title || assignment?.name || assessmentDoc?.assignmentTitle,
  )
  const curriculumAnchor = normalizeLedgerText(
    assignment?.assignmentWeekTopic || assignment?.assignmentWeekLabel,
  )

  ;(Array.isArray(assessment.rubric) ? assessment.rubric : []).forEach((item) => {
    const rubricScore = scoreFromRubricItem(item)
    addCompetencyEvidence(map, courseName, item?.label, {
      sourceType: 'assignment-rubric',
      sourceLabel: 'Assignment rubric',
      summary: item?.rationale,
      evidenceText: item?.evidence,
      actionText: item?.nextMove,
      assignmentTitle,
      curriculumAnchor,
      timestampMs: reviewedAtMs,
      score: rubricScore,
    })
  })

  ;(Array.isArray(assessment.promptFindings) ? assessment.promptFindings : []).forEach((item) => {
    const promptScore = scoreFromPromptFinding(item)
    addCompetencyEvidence(map, courseName, item?.promptLabel, {
      sourceType: 'assignment-prompt',
      sourceLabel: 'Prompt finding',
      summary: item?.responseSummary || item?.masterySignal,
      evidenceText: item?.evidence,
      actionText: item?.coachingMove,
      assignmentTitle,
      curriculumAnchor,
      timestampMs: reviewedAtMs,
      score: promptScore,
    })
  })

  ;(Array.isArray(assessment.strengths) ? assessment.strengths : []).forEach((item) => {
    addCompetencyEvidence(map, courseName, item?.title, {
      sourceType: 'assignment-strength',
      sourceLabel: 'Assignment strength',
      summary: item?.whyItMatters,
      evidenceText: item?.evidence,
      actionText: item?.keepBuildingWith,
      assignmentTitle,
      curriculumAnchor,
      timestampMs: reviewedAtMs,
      score: POSITIVE_DEFAULT_SCORE,
    })
  })

  ;(Array.isArray(assessment.growthAreas) ? assessment.growthAreas : []).forEach((item) => {
    addCompetencyEvidence(map, courseName, item?.title, {
      sourceType: 'assignment-growth',
      sourceLabel: 'Growth area',
      summary: item?.whyItMatters,
      evidenceText: item?.evidence,
      actionText: item?.intervention,
      assignmentTitle,
      curriculumAnchor,
      timestampMs: reviewedAtMs,
      score: DEVELOPING_DEFAULT_SCORE,
    })
  })

  ;(Array.isArray(assessment.misconceptions) ? assessment.misconceptions : []).forEach((item) => {
    addCompetencyEvidence(map, courseName, item?.concept, {
      sourceType: 'assignment-misconception',
      sourceLabel: 'Misconception',
      summary: item?.correction,
      evidenceText: item?.evidence,
      actionText: item?.teachingMove,
      assignmentTitle,
      curriculumAnchor,
      timestampMs: reviewedAtMs,
      score: RISK_DEFAULT_SCORE,
    })
  })
}

const addFireflySessionEvidence = (map, courseName, session = {}) => {
  const timestampMs = Number(session.endedAtMs) || Number(session.startedAtMs) || 0

  ;(Array.isArray(session.topicsCovered) ? session.topicsCovered : []).forEach((topic) => {
    addCompetencyEvidence(map, courseName, topic, {
      sourceType: 'firefly-topic',
      sourceLabel: 'Firefly topic',
      summary: session.summary || session.sessionFocus,
      evidenceText: session.sessionFocus,
      actionText: Array.isArray(session.recommendedNextSteps) ? session.recommendedNextSteps[0] : '',
      timestampMs,
      score: 72,
    })
  })

  ;(Array.isArray(session.strengths) ? session.strengths : []).forEach((item) => {
    addCompetencyEvidence(map, courseName, item, {
      sourceType: 'firefly-strength',
      sourceLabel: 'Firefly strength',
      summary: session.summary,
      actionText: Array.isArray(session.supportStrategies) ? session.supportStrategies[0] : '',
      timestampMs,
      score: POSITIVE_DEFAULT_SCORE,
    })
  })

  ;(Array.isArray(session.struggles) ? session.struggles : []).forEach((item) => {
    addCompetencyEvidence(map, courseName, item, {
      sourceType: 'firefly-struggle',
      sourceLabel: 'Firefly struggle',
      summary: session.summary,
      actionText: Array.isArray(session.supportStrategies) ? session.supportStrategies[0] : '',
      timestampMs,
      score: DEVELOPING_DEFAULT_SCORE,
    })
  })

  ;(Array.isArray(session.misconceptions) ? session.misconceptions : []).forEach((item) => {
    addCompetencyEvidence(map, courseName, item, {
      sourceType: 'firefly-misconception',
      sourceLabel: 'Firefly misconception',
      summary: session.summary,
      actionText: Array.isArray(session.recommendedNextSteps) ? session.recommendedNextSteps[0] : '',
      timestampMs,
      score: RISK_DEFAULT_SCORE,
    })
  })

  ;(Array.isArray(session.diagnoses) ? session.diagnoses : []).forEach((item) => {
    const label = normalizeLedgerText(item?.subtopic || item?.topic)
    addCompetencyEvidence(map, courseName, label, {
      sourceType: 'firefly-diagnosis',
      sourceLabel: 'Firefly diagnosis',
      summary: item?.misconception,
      evidenceText: item?.evidence,
      actionText: item?.bestIntervention,
      timestampMs,
      score: RISK_DEFAULT_SCORE,
    })
  })
}

const getSessionAffinityKeys = (session = {}) => uniqueTextList([
  ...(Array.isArray(session.topicsCovered) ? session.topicsCovered : []),
  ...(Array.isArray(session.strengths) ? session.strengths : []),
  ...(Array.isArray(session.struggles) ? session.struggles : []),
  ...(Array.isArray(session.misconceptions) ? session.misconceptions : []),
  ...(Array.isArray(session.diagnoses) ? session.diagnoses.flatMap((item) => [item?.topic, item?.subtopic]) : []),
], 30).map((item) => normalizeLedgerKey(item))

const shouldAttachSessionToCourse = (session = {}, competencyMap, courseCount = 1) => {
  if (courseCount <= 1) return true

  const sessionKeys = getSessionAffinityKeys(session)
  if (!sessionKeys.length) return false

  const competencyKeys = new Set(
    Array.from(competencyMap.values()).map((item) => item.competencyKey).filter(Boolean),
  )

  return sessionKeys.some((key) => competencyKeys.has(key))
}

const addProfileEvidence = (map, courseName, profile = {}, sourceType) => {
  const normalizedSource = sourceType === 'assignment' ? 'assignment-profile' : 'firefly-profile'
  const sourceLabel = sourceType === 'assignment' ? 'Assignment profile' : 'Firefly profile'

  const positiveSignals = sourceType === 'assignment'
    ? [
        ...extractProfileSignals(profile?.performancePatterns?.strongestRecurringDimensions, {
          score: POSITIVE_DEFAULT_SCORE,
        }),
      ]
    : [
        ...extractProfileSignals(profile?.conceptMasteryMap?.strongestThemes, {
          score: POSITIVE_DEFAULT_SCORE,
        }),
      ]

  const developingSignals = sourceType === 'assignment'
    ? [
        ...extractProfileSignals(profile?.performancePatterns?.developingDimensions, {
          score: DEVELOPING_DEFAULT_SCORE,
        }),
        ...extractProfileSignals(profile?.misconceptionLandscape, {
          score: RISK_DEFAULT_SCORE,
        }),
        ...extractProfileSignals(profile?.diagnosticLens?.misconceptionRecurrence, {
          score: RISK_DEFAULT_SCORE,
        }),
      ]
    : [
        ...extractProfileSignals(profile?.conceptMasteryMap?.fragileThemes, {
          score: DEVELOPING_DEFAULT_SCORE,
        }),
        ...extractProfileSignals(profile?.conceptMasteryMap?.misconceptionThreads, {
          score: RISK_DEFAULT_SCORE,
        }),
        ...extractProfileSignals(profile?.diagnosticLens?.misconceptionRecurrence, {
          score: RISK_DEFAULT_SCORE,
        }),
      ]

  ;[...positiveSignals, ...developingSignals].forEach((item) => {
    addCompetencyEvidence(map, courseName, item.label, {
      sourceType: normalizedSource,
      sourceLabel,
      summary: item.evidence,
      evidenceText: item.evidence,
      actionText: item.action,
      timestampMs: Date.now(),
      score: item.score,
    })
  })
}

export const buildCompetencyLedger = ({
  courseTabs = [],
  assignmentsByCourse = {},
  progressByCourse = {},
  fireflySessions = [],
  assignmentProfileData = {},
  fireflyProfileData = {},
}) => {
  const ledgerByCourse = {}

  ;(Array.isArray(courseTabs) ? courseTabs : []).forEach((courseName) => {
    const competencyMap = new Map()
    const courseProgress = progressByCourse?.[courseName] || {}
    const curriculumUnits = getCurriculumUnits(courseProgress.scheme || [])
    const completedUnitKeys = new Set(courseProgress.completedUnitLabels || [])
    const courseAssignments = Array.isArray(assignmentsByCourse?.[courseName]) ? assignmentsByCourse[courseName] : []

    curriculumUnits.forEach((unit) => {
      addCompetencyEvidence(competencyMap, courseName, unit.label, {
        sourceType: 'curriculum-anchor',
        sourceLabel: 'Curriculum anchor',
        summary: unit.parentTopic ? `Anchored in ${unit.parentTopic}` : 'Anchored in the curriculum',
        evidenceText: unit.parentTopic,
        actionText: '',
        timestampMs: 0,
      })

      if (completedUnitKeys.has(normalizeLedgerKey(unit.label))) {
        addCompetencyEvidence(competencyMap, courseName, unit.label, {
          sourceType: 'course-progress',
          sourceLabel: 'Course progress',
          summary: 'You have marked this learning unit complete in your tracked course progress.',
          evidenceText: unit.parentTopic,
          actionText: 'Keep using this concept in projects and reviewed assignments.',
          timestampMs: Number(courseProgress.updatedAtMs) || 0,
          score: 68,
        })
      }
    })

    courseAssignments.forEach((assignment) => {
      const assessmentDoc = assignment?.assessment || null

      if (assessmentDoc) {
        addAssignmentAssessmentEvidence(competencyMap, courseName, assignment, assessmentDoc)
      }
    })

    fireflySessions.forEach((session) => {
      if (shouldAttachSessionToCourse(session, competencyMap, courseTabs.length)) {
        addFireflySessionEvidence(competencyMap, courseName, session)
      }
    })

    if (assignmentProfileData?.profile) {
      addProfileEvidence(competencyMap, courseName, assignmentProfileData.profile, 'assignment')
    }

    if (fireflyProfileData?.profile) {
      addProfileEvidence(competencyMap, courseName, fireflyProfileData.profile, 'firefly')
    }

    ledgerByCourse[courseName] = finalizeCompetencies(competencyMap)
  })

  const allCompetencies = Object.values(ledgerByCourse).flat()
  const topCompetency = getTopCompetency(allCompetencies)

  return {
    ledgerByCourse,
    summary: {
      trackedCompetencies: allCompetencies.length,
      proficientOrHigher: allCompetencies.filter((item) => ['proficient', 'advanced'].includes(item.status)).length,
      developingOrEmerging: allCompetencies.filter((item) => ['developing', 'emerging'].includes(item.status)).length,
      topCompetencyLabel: topCompetency?.label || '',
    },
  }
}
